import React, { useState, useEffect } from 'react';
import { cn } from '@shared/lib/utils';

/**
 * Типы иконок, доступные в проекте
 */
export type IconName =
  | 'person'
  | 'apple'
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'arrow-up'
  | 'bell'
  | 'camera'
  | 'claim'
  | 'clip'
  | 'clock'
  | 'collapse'
  | 'edit'
  | 'email'
  | 'extend'
  | 'eye-slash'
  | 'eye'
  | 'facebook'
  | 'google'
  | 'icon'
  | 'instagram'
  | 'language'
  | 'loading'
  | 'map-pin'
  | 'menu'
  | 'order-done'
  | 'order-reviewed'
  | 'orders'
  | 'password'
  | 'pencil'
  | 'plus'
  | 'report'
  | 'settings'
  | 'star'
  | 'twitter'
  | 'variant31'
  | 'variant32'
  | 'variant35'
  | 'x';

/**
 * Размеры иконок
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Цвета иконок (семантические из дизайн-системы)
 */
export type IconColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'inverse'
  | 'disabled'
  | 'link'
  | 'accent'
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'inherit'
  | 'current';

/**
 * Маппинг имён иконок на файлы
 */
const iconFileMap: Record<IconName, string> = {
  'person': 'person.svg',
  'apple': 'apple.svg',
  'arrow-down': 'arrow-down.svg',
  'arrow-left': 'arrow-left.svg',
  'arrow-right': 'arrow-right.svg',
  'arrow-up': 'arrow-up.svg',
  'bell': 'bell.svg',
  'camera': 'camera.svg',
  'claim': 'claim.svg',
  'clip': 'clip.svg',
  'clock': 'clock.svg',
  'collapse': 'collapse.svg',
  'edit': 'edit.svg',
  'email': 'email.svg',
  'extend': 'extend.svg',
  'eye-slash': 'eye-slash.svg',
  'eye': 'eye.svg',
  'facebook': 'facebook.svg',
  'google': 'google.svg',
  'icon': 'icon.svg',
  'instagram': 'instagram.svg',
  'language': 'language.svg',
  'loading': 'loading.svg',
  'map-pin': 'map-pin.svg',
  'menu': 'menu.svg',
  'order-done': 'order-done.svg',
  'order-reviewed': 'order-reviewed.svg',
  'orders': 'orders.svg',
  'password': 'password.svg',
  'pencil': 'pencil.svg',
  'plus': 'plus.svg',
  'report': 'report.svg',
  'settings': 'settings.svg',
  'star': 'star.svg',
  'twitter': 'twitter.svg',
  'variant31': 'variant31.svg',
  'variant32': 'variant32.svg',
  'variant35': 'variant35.svg',
  'x': 'x.svg',
};

/**
 * Маппинг размеров на пиксели
 */
const sizeMap: Record<IconSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  '2xl': 48,
};

/**
 * Маппинг цветов на CSS-классы
 */
const colorMap: Record<Exclude<IconColor, 'inherit' | 'current'>, string> = {
  primary: 'text-text-primary',
  secondary: 'text-text-secondary',
  tertiary: 'text-text-tertiary',
  inverse: 'text-text-inverse',
  disabled: 'text-text-disabled',
  link: 'text-text-link',
  accent: 'text-accent-primary',
  error: 'text-feedback-error',
  success: 'text-feedback-success',
  warning: 'text-feedback-warning',
  info: 'text-feedback-info',
};

export interface IconProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  /**
   * Название иконки
   */
  name: IconName;
  
  /**
   * Размер иконки
   * @default 'md'
   */
  size?: IconSize | number;
  
  /**
   * Цвет иконки (семантический)
   * @default 'primary'
   */
  color?: IconColor;
  
  /**
   * Дополнительные классы
   */
  className?: string;
  
  /**
   * Альтернативный текст для доступности
   */
  alt?: string;
}

/**
 * Универсальный компонент иконки с поддержкой наследования цвета
 * 
 * ВАЖНО: Иконки теперь поддерживают наследование цвета через currentColor!
 * SVG встраивается напрямую и использует fill="currentColor".
 * 
 * @example
 * // Базовое использование
 * <Icon name="bell" />
 * 
 * @example
 * // С кастомным размером и цветом
 * <Icon name="star" size="lg" color="accent" />
 * 
 * @example
 * // С числовым размером
 * <Icon name="menu" size={28} />
 * 
 * @example
 * // Наследование цвета от родителя (для использования в кнопках)
 * <button className="text-white">
 *   <Icon name="google" color="inherit" />
 * </button>
 * 
 * @example
 * // С дополнительными классами и событиями
 * <Icon 
 *   name="close" 
 *   size="sm" 
 *   color="secondary"
 *   className="cursor-pointer hover:opacity-80 transition-opacity"
 *   onClick={handleClose}
 * />
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 'md',
  color = 'primary',
  className,
  alt,
  ...props
}) => {
  const [svgContent, setSvgContent] = useState<string>('');
  
  // Определяем размер в пикселях
  const pixelSize = typeof size === 'number' ? size : sizeMap[size];
  
  // Определяем CSS-класс для цвета
  const colorClass = color === 'inherit' 
    ? 'text-inherit' 
    : color === 'current'
    ? 'text-current'
    : colorMap[color];
  
  // Получаем путь к файлу иконки
  const iconFile = iconFileMap[name];
  const iconPath = `/images/icons/${iconFile}`;
  
  // Загружаем SVG контент
  useEffect(() => {
    fetch(iconPath)
      .then(res => res.text())
      .then(svg => {
        // Добавляем currentColor к SVG если его там нет
        let svgWithColor = svg.replace(/fill="[^"]*"/g, 'fill="currentColor"');

        // Удаляем существующие width и height атрибуты, затем добавляем width="100%" и height="100%"
        // чтобы SVG масштабировался до размера контейнера
        svgWithColor = svgWithColor.replace(
          /<svg([^>]*)>/,
          (_match, attributes) => {
            // Удаляем существующие width и height
            const cleanedAttributes = attributes
              .replace(/\s*width="[^"]*"/g, '')
              .replace(/\s*height="[^"]*"/g, '');
            return `<svg${cleanedAttributes} width="100%" height="100%">`;
          }
        );

        setSvgContent(svgWithColor);
      })
      .catch(err => console.error(`Failed to load icon ${name}:`, err));
  }, [iconPath, name]);
  
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center flex-shrink-0 overflow-hidden',
        colorClass,
        className
      )}
      style={{
        width: pixelSize,
        height: pixelSize,
      }}
      role="img"
      aria-label={alt || name}
      dangerouslySetInnerHTML={{ __html: svgContent }}
      {...props}
    />
  );
};

Icon.displayName = 'Icon';
